import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  search as searchAPI,
  stroot,
  GlobalSearchBounds,
  GlobalSearchPin,
  GlobalSearchResult,
  GlobalSearchResultType,
} from '@birdi/js-sdk';

const DEBOUNCE_MS = 120;
/** Per type, so at least this many of the most recent of EACH show up. */
export const PAGE_SIZE = 10;
export const RESULT_TYPES: GlobalSearchResultType[] = [
  'map',
  'mission',
  'dataset',
];

export type ViewportBbox = [number, number, number, number];

export interface InitialScope {
  /** Seed the viewport filter with this extent. */
  bbox: ViewportBbox | null;
  /** The map search was opened from; pinned first on the initial list. */
  mapId: string | null;
}

export interface TypePage {
  results: GlobalSearchResult[];
  hasMore: boolean;
  /** Page one in flight (the tile shows its skeleton / the globe spinner). */
  loading: boolean;
  /** A further page appending (the tile shows a small footer). */
  loadingMore: boolean;
}

export type ResultsByType = Record<GlobalSearchResultType, TypePage>;

const emptyPage = (): TypePage => ({
  results: [],
  hasMore: false,
  loading: false,
  loadingMore: false,
});
const emptyByType = (): ResultsByType => ({
  map: emptyPage(),
  mission: emptyPage(),
  dataset: emptyPage(),
});

/** Envelope of several result-set bounds (null when none). */
const mergeBounds = (
  all: (GlobalSearchBounds | null)[],
): GlobalSearchBounds | null => {
  const present = all.filter((b): b is GlobalSearchBounds => b !== null);
  if (present.length === 0) return null;
  return present.reduce(([[w, s], [e, n]], [[w2, s2], [e2, n2]]) => [
    [Math.min(w, w2), Math.min(s, s2)],
    [Math.max(e, e2), Math.max(n, n2)],
  ]);
};

/**
 * Debounced, abortable global search, paged PER TYPE (PRD): maps, missions
 * and datasets are three independent requests of PAGE_SIZE with their own
 * loadMore (the tile pulls the next page in as you scroll), so a busy type
 * never starves the others. The query,
 * viewport filter or workspace filter changing resets every type to page
 * one; an in-flight request is aborted on every change so a slow earlier
 * response can never overwrite a newer one.
 */
export const useGlobalSearch = (
  initialBbox: ViewportBbox | null = null,
  pinMapId: string | null = null,
) => {
  const [query, setQuery] = useState('');
  // Opening from a map/mission seeds the viewport filter with its extent.
  const [viewportBbox, setViewportBbox] = useState<ViewportBbox | null>(
    initialBbox,
  );
  // Optional workspace filter (null = every workspace the user belongs to).
  const [workspaceId, setWorkspaceId] = useState<string | null>(null);
  const [byType, setByType] = useState<ResultsByType>(emptyByType);
  const [boundsByType, setBoundsByType] = useState<
    Record<GlobalSearchResultType, GlobalSearchBounds | null>
  >({ map: null, mission: null, dataset: null });
  const [pins, setPins] = useState<GlobalSearchPin[]>([]);
  const controllersRef = useRef<
    Record<GlobalSearchResultType, AbortController | null>
  >({ map: null, mission: null, dataset: null });

  // All accessible map/mission pins (within the workspace filter, when set).
  useEffect(() => {
    let cancelled = false;
    searchAPI
      .getPins(stroot('gsrch'), { workspaceId: workspaceId ?? undefined })
      .then((res) => {
        if (!cancelled && res.body) setPins(res.body.pins);
      })
      .catch(() => {
        /* pins are decorative; searching still works without them */
      });
    return () => {
      cancelled = true;
    };
  }, [workspaceId]);

  const requestOpts = useCallback(
    (
      type: GlobalSearchResultType,
      term: string,
      offset: number,
      signal?: AbortSignal,
    ) => ({
      limit: PAGE_SIZE,
      offset,
      type,
      bbox: viewportBbox ?? undefined,
      workspaceId: workspaceId ?? undefined,
      // Only the map the overlay was opened from, only on page one of the
      // initial (no term) list. With a term typed it would sit above the
      // matches, so Enter or a click on the top row reopened the same map.
      pinMapId:
        type === 'map' && offset === 0 && !term
          ? (pinMapId ?? undefined)
          : undefined,
      ...(signal ? { signal } : {}),
    }),
    [viewportBbox, workspaceId, pinMapId],
  );

  // Page one for every type — debounced on query, immediate on filters. An
  // empty query is browse mode (maps and missions; datasets stay empty).
  useEffect(() => {
    const trimmed = query.trim();
    const types = trimmed
      ? RESULT_TYPES
      : RESULT_TYPES.filter((t) => t !== 'dataset');
    setByType((prev) => {
      const next = { ...prev };
      RESULT_TYPES.forEach((t) => {
        next[t] = types.includes(t)
          ? { ...prev[t], loading: true, loadingMore: false }
          : emptyPage();
      });
      return next;
    });
    const timer = setTimeout(() => {
      types.forEach((type) => {
        controllersRef.current[type]?.abort();
        const controller = new AbortController();
        controllersRef.current[type] = controller;
        searchAPI
          .search(
            stroot('gsrch'),
            trimmed,
            requestOpts(type, trimmed, 0, controller.signal),
          )
          .then((res) => {
            if (controller.signal.aborted) return;
            setByType((prev) => ({
              ...prev,
              [type]: {
                results: res.body?.results ?? [],
                hasMore: res.body?.hasMore ?? false,
                loading: false,
                loadingMore: false,
              },
            }));
            setBoundsByType((prev) => ({
              ...prev,
              [type]: res.body?.bounds ?? null,
            }));
          })
          .catch(() => {
            if (controller.signal.aborted) return;
            setByType((prev) => ({
              ...prev,
              [type]: { ...prev[type], loading: false },
            }));
          });
      });
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query, requestOpts]);

  // Next page for ONE type appends; guarded against parallel loads.
  const loadMore = useCallback(
    (type: GlobalSearchResultType) => {
      const page = byType[type];
      if (!page.hasMore || page.loading || page.loadingMore) return;
      setByType((prev) => ({
        ...prev,
        [type]: { ...prev[type], loadingMore: true },
      }));
      const controller = controllersRef.current[type];
      searchAPI
        .search(
          stroot('gsrch'),
          query.trim(),
          requestOpts(type, query.trim(), page.results.length),
        )
        .then((res) => {
          // A newer page-one request supersedes this append.
          if (controller !== controllersRef.current[type]) return;
          setByType((prev) => ({
            ...prev,
            [type]: {
              ...prev[type],
              results: [...prev[type].results, ...(res.body?.results ?? [])],
              hasMore: res.body?.hasMore ?? false,
              loadingMore: false,
            },
          }));
        })
        .catch(() => {
          // Scrolling again retries.
          if (controller !== controllersRef.current[type]) return;
          setByType((prev) => ({
            ...prev,
            [type]: { ...prev[type], loadingMore: false },
          }));
        });
    },
    [byType, query, requestOpts],
  );

  useEffect(
    () => () => {
      RESULT_TYPES.forEach((t) => controllersRef.current[t]?.abort());
    },
    [],
  );

  const bounds = useMemo(
    () => mergeBounds(RESULT_TYPES.map((t) => boundsByType[t])),
    [boundsByType],
  );
  const loading = RESULT_TYPES.some((t) => byType[t].loading);

  return {
    query,
    setQuery,
    viewportBbox,
    setViewportBbox,
    workspaceId,
    setWorkspaceId,
    byType,
    bounds,
    loadMore,
    pins,
    loading,
  };
};
